import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPanelTitle,
  AdminSupportingText,
  AdminAlertText,
  AdminHeaderAction,
  AdminStatusBadge,
  AdminMetadataText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminTabButton,
  AdminLinkAction,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  ADMIN_TAB_CONTENT_CONTAINER_CLASS,
} from '../shared/adminUx';
import { financeTransactions } from './financeData';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(amount || 0);

const formatDateTime = (isoString) => {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatDateOnly = (isoString) => {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getStatusDisplay = (status) => {
  if (status === 'Success') return 'Succeeded';
  return status || '—';
};

const getTypeDisplay = (txn) => {
  if (!txn) return '—';
  if (txn.isChargeback) return 'Chargeback';
  if (txn.status === 'Failed') return 'Failed Payment';
  if (txn.status === 'Refunded') return 'Refund';
  if (txn.status === 'Disputed') return 'Dispute';
  return 'Subscription Charge';
};

const getBillingPeriod = (txn) => {
  if (!txn?.date) return '—';
  const start = new Date(txn.date);
  const end = txn.renewalDate ? new Date(txn.renewalDate) : new Date(start);
  if (!txn.renewalDate) end.setMonth(end.getMonth() + 1);

  const startText = start.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const endText = end.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  return `${startText} → ${endText}`;
};

/** Hosted receipt URL from Stripe, or Stripe Dashboard payment page when only pi_ id is known (admin view). */
const getStripeReceiptHref = (txn) => {
  if (!txn) return null;
  if (txn.stripeReceiptUrl) return txn.stripeReceiptUrl;
  const pi = txn.stripeTransactionId;
  if (!pi || typeof pi !== 'string' || !pi.startsWith('pi_')) return null;
  const eligible = ['Success', 'Refunded', 'Disputed', 'Pending'].includes(txn.status);
  if (!eligible) return null;
  return `https://dashboard.stripe.com/payments/${pi}`;
};

const getFailureReasonDisplay = (responseCode) => {
  if (!responseCode) return '—';
  const map = {
    card_declined: 'Card Declined',
    insufficient_funds: 'Insufficient Funds',
    do_not_honor: 'Do Not Honor',
    expired_card: 'Expired Card',
    authentication_required: 'Authentication Required',
    requires_action: 'Requires Action',
  };
  return map[responseCode] || responseCode.replace(/_/g, ' ');
};

const TABS = [{ id: 'overview', label: 'Overview' }];

const BillingTransactionDetail = () => {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [transaction, setTransaction] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const passedTransaction = location.state?.transaction;
    if (passedTransaction) {
      setTransaction(passedTransaction);
      return;
    }
    const foundTransaction = financeTransactions.find((txn) => txn.id === transactionId);
    setTransaction(foundTransaction || null);
  }, [transactionId, location.state]);

  if (!transaction) {
    return (
      <AdminLayout>
        <DashboardMainContainer className="!bg-white px-6 py-5">
          <div className="text-center space-y-4 py-16">
            <AdminAlertText>Transaction not found</AdminAlertText>
            <AdminHeaderAction className="!h-[36px] !px-4" onClick={() => navigate('/admin/billing/transactions')}>
              Back to Billing & Subscriptions
            </AdminHeaderAction>
          </div>
        </DashboardMainContainer>
      </AdminLayout>
    );
  }

  const statusDisplay = getStatusDisplay(transaction.status);
  const typeDisplay = getTypeDisplay(transaction);
  const stripeReceiptHref = getStripeReceiptHref(transaction);
  const outcomeRows = [];

  if (typeDisplay === 'Failed Payment') {
    outcomeRows.push({ label: 'Failure Reason', value: getFailureReasonDisplay(transaction.responseCode) });
  } else if (typeDisplay === 'Refund') {
    outcomeRows.push({ label: 'Refund Status', value: transaction.refund?.status || 'Refunded' });
  } else if (typeDisplay === 'Dispute') {
    outcomeRows.push({ label: 'Dispute Status', value: transaction.dispute?.status || 'In Review' });
  } else if (typeDisplay === 'Chargeback') {
    outcomeRows.push({ label: 'Chargeback Status', value: transaction.dispute?.status || 'In Review' });
  } else {
    outcomeRows.push({ label: 'Outcome', value: 'No exceptions' });
  }

  return (
    <AdminLayout>
      <div className="tp-container-main">
      <div>
        <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
          <div>
            <AdminPanelTitle className="mb-3">{transaction.id}</AdminPanelTitle>
            <div className="flex items-center gap-4">
              <AdminMetadataText className="text-offblack">{formatDateTime(transaction.date)}</AdminMetadataText>
              <AdminStatusBadge status={statusDisplay} label={statusDisplay} />
            </div>
          </div>
        </DashboardMainContainer>

        <DashboardMainContainer className="!bg-white">
          <div className="border-b border-lightgray">
            <div className="flex space-x-8 overflow-x-auto">
              {TABS.map((tab) => (
                <AdminTabButton
                  key={tab.id}
                  active={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </AdminTabButton>
              ))}
            </div>
          </div>

          <div className={ADMIN_TAB_CONTENT_CONTAINER_CLASS}>
            {activeTab === 'overview' && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="space-y-4">
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Transaction ID</AdminDetailFieldLabel>
                  <AdminDetailFieldValue>{transaction.id}</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Type</AdminDetailFieldLabel>
                  <AdminDetailFieldValue>{typeDisplay}</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Amount</AdminDetailFieldLabel>
                  <AdminDetailFieldValue>{formatCurrency(transaction.amount)}</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Status</AdminDetailFieldLabel>
                  <AdminDetailFieldValue>{statusDisplay}</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Transaction Date</AdminDetailFieldLabel>
                  <AdminDetailFieldValue>{formatDateTime(transaction.date)}</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Billing Period</AdminDetailFieldLabel>
                  <AdminDetailFieldValue>{getBillingPeriod(transaction)}</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Plan</AdminDetailFieldLabel>
                  <AdminDetailFieldValue>{transaction.planKey || 'N/A'}</AdminDetailFieldValue>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Business Name</AdminDetailFieldLabel>
                  <AdminDetailFieldValue>{transaction.businessName || '—'}</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Business ID</AdminDetailFieldLabel>
                  <AdminDetailFieldValue>{transaction.businessId || '—'}</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Account Status</AdminDetailFieldLabel>
                  <AdminDetailFieldValue>{transaction.accountStatus || 'Active'}</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Joined On</AdminDetailFieldLabel>
                  <AdminDetailFieldValue>{transaction.memberSince ? formatDateOnly(transaction.memberSince) : '—'}</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Subscription Status</AdminDetailFieldLabel>
                  <AdminDetailFieldValue>{transaction.subscriptionStatus || 'Active'}</AdminDetailFieldValue>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Payment Method</AdminDetailFieldLabel>
                  <AdminDetailFieldValue>
                    {transaction.paymentBrand && transaction.paymentLast4
                      ? `${transaction.paymentBrand} xxxx-${transaction.paymentLast4}`
                      : '—'}
                  </AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Provider</AdminDetailFieldLabel>
                  <AdminDetailFieldValue>Stripe</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Provider Reference</AdminDetailFieldLabel>
                  <AdminDetailFieldValue break-all>
                    {transaction.stripeTransactionId || '—'}
                  </AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Invoice Reference</AdminDetailFieldLabel>
                  <AdminDetailFieldValue>{transaction.invoiceNumber || '—'}</AdminDetailFieldValue>
                </div>
                <div className="space-y-1">
                  <AdminDetailFieldLabel>Stripe Receipt</AdminDetailFieldLabel>
                  {stripeReceiptHref ? (
                    <AdminLinkAction
                      href={stripeReceiptHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium inline-flex items-center gap-1"
                    >
                      View Stripe receipt
                      <i className="fi fi-rr-arrow-small-right text-xs" aria-hidden />
                    </AdminLinkAction>
                  ) : (
                    <AdminDetailFieldValue>—</AdminDetailFieldValue>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                {outcomeRows.map((item) => (
                  <div className="space-y-1" key={item.label}>
                    <AdminDetailFieldLabel>{item.label}</AdminDetailFieldLabel>
                    <AdminDetailFieldValue>{item.value}</AdminDetailFieldValue>
                  </div>
                ))}
              </div>
                </div>
              </div>
            )}
          </div>
        </DashboardMainContainer>
      </div>
      </div>
    </AdminLayout>
  );
};

export default BillingTransactionDetail;
